import { useEffect, useState } from "react";
import { Menu, MenuItem } from "@mui/material";
import { useAuthContext } from "@/context/auth/useAuthContext";
import Iconify from "@/components/ui/Iconify";
import { createPostPin, deletePostPin, updatePost } from "@/api/posts";
import { enqueueSnackbar } from "notistack";
import { useTranslations } from "@/lib/static-intl";
import { useTheme } from "next-themes";

const PostDetailsControls = ({
  setAllPosts,
  selectedPostInModal,
  setSelectedPostInModal,
  openDeletePostWarningModal,
}) => {
  const { theme } = useTheme();
  const apiT = useTranslations("api");
  const explorePostDetailsT = useTranslations("explore.explore_post_details");

  const { user, openLoginModal } = useAuthContext();
  const [isPublic, setIsPublic] = useState(false);
  const [postMenuAnchorEl, setPostMenuAnchorEl] = useState(null);
  const postMenuOpen = Boolean(postMenuAnchorEl);

  const handlePostMenuClick = (event) => {
    if (!user) {
      openLoginModal();
      return;
    }
    setPostMenuAnchorEl(event.currentTarget);
  };

  const handlePostMenuClose = () => {
    if (!user) {
      openLoginModal();
      return;
    }
    setPostMenuAnchorEl(null);
  };

  const handlePostPinning = async () => {
    try {
      const payload = {
        postId: selectedPostInModal?.id,
        userId: user?.userId,
      };

      if (!user) openLoginModal();
      if (!payload?.postId || !payload?.userId) return;

      setAllPosts((prev) =>
        prev.map((post) => {
          if (post?.id === payload?.postId) {
            return {
              ...post,
              pinnedPosts: [...(post?.pinnedPosts || []), payload],
            };
          }
          return post;
        })
      );
      setSelectedPostInModal({
        ...selectedPostInModal,
        pinnedPosts: [...(selectedPostInModal?.pinnedPosts || {}), payload],
      });
      handlePostMenuClose();
      await createPostPin(payload);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        {
          variant: "error",
        }
      );
    }
  };

  const handlePostUnpinning = async () => {
    try {
      const postId = selectedPostInModal?.id;
      const userId = user?.userId;

      if (!userId) openLoginModal();

      if (!postId || !userId) return;

      setAllPosts((prev) =>
        prev.map((post) => {
          if (post?.id === postId) {
            return {
              ...post,
              pinnedPosts: post?.pinnedPosts?.filter(
                (pinnedPost) => pinnedPost?.userId !== userId
              ),
            };
          }
          return post;
        })
      );
      setSelectedPostInModal({
        ...selectedPostInModal,
        pinnedPosts: selectedPostInModal?.pinnedPosts?.filter(
          (pinnedPost) => pinnedPost?.userId !== userId
        ),
      });
      handlePostMenuClose();
      await deletePostPin(postId, userId);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        {
          variant: "error",
        }
      );
    }
  };

  const handlePostVisibility = async () => {
    try {
      if (!user) openLoginModal();

      if (!selectedPostInModal?.id || !user?.userId) return;

      const isPublic = selectedPostInModal?.isPublic;

      const payload = {
        isPublic: !isPublic,
      };

      setAllPosts((prev) =>
        prev.map((post) => {
          if (post?.id === selectedPostInModal?.id) {
            return {
              ...post,
              isPublic: !isPublic,
            };
          }
          return post;
        })
      );

      setSelectedPostInModal({
        ...selectedPostInModal,
        isPublic: !isPublic,
      });
      handlePostMenuClose();

      await updatePost(payload, selectedPostInModal?.id);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        {
          variant: "error",
        }
      );
    }
  };

  const handlePostDelete = () => {
    openDeletePostWarningModal();
    handlePostMenuClose();
  };

  const handlePostEdit = (postId) => {
    if (postId) {
      window.open("/creator/upload/edit/" + postId, "_blank");
      handlePostMenuClose();
    }
  };

  useEffect(() => {
    setIsPublic(selectedPostInModal?.isPublic);
  }, [selectedPostInModal?.isPublic]);

  return (
    <div>
      <button
        id="post-menu"
        onClick={handlePostMenuClick}
        className="flex items-center gap-3 text-sm py-1.5 px-3 rounded-full hover:bg-black hover:bg-opacity-5 transition"
      >
        <Iconify
          icon={"uiw:down"}
          className={`transition !w-3 !h-3 ${postMenuOpen ? "rotate-180" : ""}`}
        />
        {explorePostDetailsT("options")}
      </button>
      <Menu
        id="post-menu"
        anchorEl={postMenuAnchorEl}
        open={postMenuOpen}
        onClose={handlePostMenuClose}
        MenuListProps={{
          "aria-labelledby": "post-menu",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        style={{
          zIndex: 9999,
        }}
        sx={{
          "& .MuiMenu-list": {
            bgcolor: theme === "dark" ? "#212121" : "white",
            py: 1,
            border: "transparent",
          },
          "& .MuiPaper-root": {
            zIndex: 9999,
          },
        }}
      >
        {selectedPostInModal?.pinnedPosts?.find(
          (_pf) => _pf?.userId === user?.userId
        ) ? (
          <MenuItem
            key="unpin"
            onClick={handlePostUnpinning}
            sx={{ py: 0.5, fontSize: 14 }}
          >
            <div className="text-inherit flex items-center gap-1 dark:text-white">
              <Iconify
                icon="fluent-mdl2:unpin"
                className={"!w-4 !h-4 rotate-45"}
              />
              {explorePostDetailsT("unpin")}
            </div>
          </MenuItem>
        ) : (
          <MenuItem
            key="pin"
            onClick={handlePostPinning}
            sx={{ py: 0.5, fontSize: 14 }}
          >
            <div className="text-inherit flex items-center gap-1 dark:text-white">
              <Iconify icon="fluent-mdl2:pin" className={"!w-4 !h-4"} />
              {explorePostDetailsT("pin_to_top")}
            </div>
          </MenuItem>
        )}
        {isPublic ? (
          <MenuItem
            key="private"
            onClick={handlePostVisibility}
            sx={{ py: 0.5, fontSize: 14 }}
          >
            <div className="text-inherit flex items-center gap-1 dark:text-white">
              <Iconify icon="clarity:eye-hide-line" className={"!w-4 !h-4"} />
              {explorePostDetailsT("invisible")}
            </div>
          </MenuItem>
        ) : (
          <MenuItem
            key="public"
            onClick={handlePostVisibility}
            sx={{ py: 0.5, fontSize: 14 }}
          >
            <div className="text-inherit flex items-center gap-1 dark:text-white">
              <Iconify icon="clarity:eye-show-line" className={"!w-4 !h-4"} />
              {explorePostDetailsT("visible")}
            </div>
          </MenuItem>
        )}
        <MenuItem
          key="edit"
          sx={{ py: 0.5, fontSize: 14 }}
          onClick={() => handlePostEdit(selectedPostInModal?.id)}
        >
          <div className="text-inherit flex items-center gap-1 dark:text-white">
            <Iconify
              icon="fluent:notepad-edit-16-regular"
              className={"!w-4 !h-4"}
            />
            {explorePostDetailsT("edit")}
          </div>
        </MenuItem>
        <MenuItem
          key="delete"
          onClick={handlePostDelete}
          sx={{ py: 0, fontSize: 14 }}
        >
          <div className="text-inherit flex items-center gap-1 dark:text-white">
            <Iconify
              icon="material-symbols-light:delete-outline"
              className={"!w-4 !h-4"}
            />
            {explorePostDetailsT("delete")}
          </div>
        </MenuItem>
      </Menu>
    </div>
  );
};

export default PostDetailsControls;
